import React, { useMemo } from 'react';
import { Modal, Form, Input, Select, Button, Space, Radio, InputNumber, Alert } from 'antd';

// DataSet 查詢條件編輯 Modal
// 條件值支援：固定值 / 選擇變量 / 動態注入用戶自然語言
const DataSetQueryConditionEditModal = ({
  visible,
  onCancel,
  editingCondition,
  conditionForm,
  dataSetColumns,
  processVariables,
  onSave,
  t
}) => {
  const valueSource = Form.useWatch('valueSource', conditionForm) ?? 'fixed';
  const selectedVariableRef = Form.useWatch('value', conditionForm); // 變量模式下為 ${varName}
  const operator = Form.useWatch('operator', conditionForm);
  const isNlMode = valueSource === 'userNaturalLanguage';
  const valueNotRequired = operator === 'isEmpty' || operator === 'isNotEmpty' || isNlMode;
  const valueRequiredRule = valueNotRequired ? [] : [{ required: true, message: t('workflowDesigner.dataSet.conditionValuePlaceholder') }];

  const selectedPv = useMemo(() => {
    if (valueSource !== 'variable' || !selectedVariableRef || !processVariables?.length) return null;
    const variableName = (selectedVariableRef || '').replace(/^\$\{/, '').replace(/\}$/, '');
    return processVariables.find(pv => (pv.variableName || pv.name) === variableName) ?? null;
  }, [valueSource, selectedVariableRef, processVariables]);

  const isJsonPv = selectedPv?.dataType?.toLowerCase() === 'json';

  const variableOptions = useMemo(() => {
    if (!processVariables?.length) return [];
    return processVariables.map(pv => ({
      value: `\${${pv.variableName || pv.name}}`,
      label: `${pv.variableName || pv.name} (${pv.dataType || 'Unknown'})`
    }));
  }, [processVariables]);

  return (
    <Modal
      title={t('workflowDesigner.dataSet.editQueryCondition')}
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={null}
      destroyOnClose
    >
      <Form
        form={conditionForm}
        layout="vertical"
        onFinish={onSave}
      >
        <Form.Item
          label={t('workflowDesigner.dataSet.selectDataSetField')}
          name="fieldName"
          rules={[{ required: true, message: t('workflowDesigner.dataSet.selectDataSetFieldPlaceholder') }]}
        >
          <Select placeholder={t('workflowDesigner.dataSet.selectDataSetField')}>
            {dataSetColumns.length > 0 ? (
              dataSetColumns.map(col => (
                <Select.Option key={col.columnName} value={col.columnName}>
                  {col.displayName || col.columnName} ({col.dataType})
                </Select.Option>
              ))
            ) : (
              <Select.Option value="" disabled>
                {t('workflowDesigner.dataSet.noDataSetFields')}
              </Select.Option>
            )}
          </Select>
          {dataSetColumns.length === 0 && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {t('workflowDesigner.dataSet.pleaseSelectDataSet')}
            </div>
          )}
        </Form.Item>
        
        {/* 操作符：NL 模式下固定為「包含於用戶訊息」，不需要用戶選擇 */}
        {!isNlMode && (
          <Form.Item
            label={t('workflowDesigner.dataSet.operator')}
            name="operator"
            rules={[{ required: true, message: t('workflowDesigner.dataSet.operatorPlaceholder') }]}
          >
            <Select>
              <Select.Option value="equals">等於 (=)</Select.Option>
              <Select.Option value="notEquals">不等於 (!=)</Select.Option>
              <Select.Option value="greaterThan">大於 (>)</Select.Option>
              <Select.Option value="lessThan">小於 (&lt;)</Select.Option>
              <Select.Option value="greaterThanOrEqual">大於等於 (&gt;=)</Select.Option>
              <Select.Option value="lessThanOrEqual">小於等於 (&lt;=)</Select.Option>
              <Select.Option value="contains">包含 (LIKE %value%)</Select.Option>
              <Select.Option value="startsWith">開始於 (LIKE value%)</Select.Option>
              <Select.Option value="endsWith">結束於 (LIKE %value)</Select.Option>
              <Select.Option value="isEmpty">為空 (IS NULL OR = '')</Select.Option>
              <Select.Option value="isNotEmpty">不為空 (IS NOT NULL AND != '')</Select.Option>
              <Select.Option value="in">在列表中 (IN)</Select.Option>
              <Select.Option value="notIn">不在列表中 (NOT IN)</Select.Option>
            </Select>
          </Form.Item>
        )}
        
        {/* 條件值來源：固定值 / 選擇變量 / 動態注入用戶自然語言 */}
        <Form.Item
          label={t('workflowDesigner.dataSet.conditionValueSource')}
          name="valueSource"
          initialValue="fixed"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="fixed">{t('workflowDesigner.dataSet.fixedValue')}</Radio>
            <Radio value="variable">{t('workflowDesigner.dataSet.selectVariable')}</Radio>
            <Radio value="userNaturalLanguage">{t('workflowDesigner.dataSet.userNaturalLanguage')}</Radio>
          </Radio.Group>
        </Form.Item>
        
        {valueSource === 'fixed' && (
          <Form.Item
            label={t('workflowDesigner.dataSet.conditionValue')}
            name="value"
            rules={valueRequiredRule}
          >
            <Input placeholder={t('workflowDesigner.dataSet.conditionValueInputPlaceholder')} />
          </Form.Item>
        )}
        
        {valueSource === 'variable' && (
          <>
            <Form.Item
              label={t('workflowDesigner.dataSet.conditionValue')}
              name="value"
              rules={valueRequiredRule}
            >
              <Select
                placeholder={t('workflowDesigner.dataSet.selectProcessVariable')}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={variableOptions}
                allowClear
              />
            </Form.Item>
            {isJsonPv && (
              <Form.Item
                label={t('workflowDesigner.dataSet.jsonPvMapping')}
                name="valueJsonKey"
              >
                <Input
                  placeholder={t('workflowDesigner.dataSet.jsonKeyPlaceholder')}
                  prefix={<span style={{ color: '#999' }}>JSON Key:</span>}
                />
              </Form.Item>
            )}
            {isJsonPv && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '-8px', marginBottom: '16px' }}>
                {t('workflowDesigner.dataSet.jsonKeyDescription')}
              </div>
            )}
          </>
        )}

        {/* 動態注入用戶自然語言模式設定 */}
        {isNlMode && (
          <div style={{
            background: '#f6f8ff',
            border: '1px solid #d0d9ff',
            borderRadius: 6,
            padding: '12px 16px',
            marginBottom: 16
          }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('workflowDesigner.dataSet.nlModeDescription')}
            />

            <Form.Item
              label={t('workflowDesigner.dataSet.nlFallback')}
              name="nlFallback"
              initialValue="keepAll"
              style={{ marginBottom: 8 }}
            >
              <Radio.Group>
                <Radio value="keepAll">{t('workflowDesigner.dataSet.nlFallbackKeepAll')}</Radio>
                <Radio value="returnEmpty">{t('workflowDesigner.dataSet.nlFallbackReturnEmpty')}</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label={t('workflowDesigner.dataSet.nlMinFieldLength')}
              name="nlMinFieldLength"
              initialValue={2}
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={1} max={20} style={{ width: 80 }} />
            </Form.Item>
            <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>
              {t('workflowDesigner.dataSet.nlMinFieldLengthHint')}
            </div>
          </div>
        )}
        
        <Form.Item
          label={t('workflowDesigner.dataSet.conditionLabel')}
          name="label"
        >
          <Input placeholder={t('workflowDesigner.dataSet.conditionLabelPlaceholder')} />
        </Form.Item>
        
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              {t('workflowDesigner.dataSet.cancel')}
            </Button>
            <Button type="primary" htmlType="submit">
              {editingCondition && editingCondition.condIndex !== -1 ? t('workflowDesigner.dataSet.update') : t('workflowDesigner.dataSet.add')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default DataSetQueryConditionEditModal;
